import React from "react";
import { useLocation, useNavigate, matchPath } from "react-router-dom";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  useMediaQuery,
  Typography,
  Stack,
  Theme,
  alpha,
} from "@mui/material";
import { Adjust as AdjustIcon } from "@mui/icons-material";
import { navigationSections, NavigationSection } from "../config/navigationConfig";
import { APP_CONFIG } from "../utils/constants";

const DRAWER_WIDTH = 260;

interface SideNavProps {
  onClose: () => void;
  open: boolean;
}

export const SideNav: React.FC<SideNavProps> = ({ onClose, open }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMdUp = useMediaQuery("(min-width:900px)");

  const renderNavItems = (items: any[], level = 0) => (
    <List disablePadding>
      {items.map((item) => {
        const isActive = !!matchPath({ path: item.path, end: false }, location.pathname);

        return (
          <React.Fragment key={item.label}>
            <ListItemButton
              sx={{
                pr: 1,
                pl: 1 + level * 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: isActive
                  ? (theme: Theme) => alpha(theme.palette.primary.main, 0.15)
                  : "transparent",
              }}
              onClick={() => {
                if (item.comingSoon) return;
                navigate(item.path);
                if (!isMdUp) onClose();
              }}
            >
              {item.icon && (
                <ListItemIcon
                  sx={{
                    minWidth: 32,
                    color: isActive ? "primary.main" : "text.secondary",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
              )}
              <ListItemText
                primary={item.label}
                slotProps={{
                  primary: {
                    sx: {
                      fontSize: 14,
                      color: isActive ? "primary.main" : "text.secondary",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  },
                }}
              />
            </ListItemButton>
          </React.Fragment>
        );
      })}
    </List>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { md: open ? DRAWER_WIDTH : 0 },
        flexShrink: { md: 0 },
        transition: "width 0.3s ease",
      }}
    >
      <Drawer
        variant={isMdUp ? "persistent" : "temporary"}
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            border: "none",
            transition: "width 0.3s ease",
            backgroundColor: (theme) =>
              !isMdUp ? theme.palette.background.default : "transparent",
          },
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1,
                backgroundColor: (theme: Theme) => theme.palette.primary.main,
                color: (theme: Theme) => theme.palette.primary.contrastText,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AdjustIcon />
            </Box>
            <Stack spacing={0}>
              <Typography variant="subtitle1" fontWeight={600}>
                {APP_CONFIG.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {APP_CONFIG.description}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {/* Navigation content */}
        <Box
          sx={{
            mt: 1,
            overflowY: "auto",
            flex: 1,
            gap: 1,
            display: "flex",
            flexDirection: "column",
            px: 2,
          }}
        >
          {navigationSections.map((section: NavigationSection, idx: number) => (
            <Box key={idx}>
              {section.smallTitle && (
                <Typography variant="overline" sx={{ color: "text.secondary" }}>
                  {section.smallTitle}
                </Typography>
              )}
              {renderNavItems(section.items)}
            </Box>
          ))}
        </Box>
      </Drawer>
    </Box>
  );
};
